import type { BrokerCapabilities } from "@/services/brokerIntegrationApi"
import { isCustomCsvBroker } from "@/services/brokerIntegrationApi"
import { resolveBrokerCsvImport } from "@/utils/brokerTradeSync"
import { isMt4Broker } from "@/utils/mt4Broker"
import { isMt5Broker } from "@/utils/mt5Broker"

export type ConnectBrokerImportMethodId = "auto_sync" | "file_upload" | "manual"

export const CONNECT_BROKER_AUTO_SYNC_LABEL = "Broker sync"

export type ConnectBrokerImportMethodOption = {
  id: ConnectBrokerImportMethodId
  title: string
  description: string
}

const AUTO_SYNC_OPTION: ConnectBrokerImportMethodOption = {
  id: "auto_sync",
  title: CONNECT_BROKER_AUTO_SYNC_LABEL,
  description:
    "Connect credentials and sync recent trades from your broker.",
}

export const CONNECT_BROKER_FILE_UPLOAD_OPTION: ConnectBrokerImportMethodOption = {
  id: "file_upload",
  title: "File upload",
  description:
    "Import a statement or trade history file (CSV, XLSX, or HTML).",
}

const MT5_FILE_UPLOAD_OPTION: ConnectBrokerImportMethodOption = {
  id: "file_upload",
  title: "File upload",
  description: "Import a MetaTrader 5 account history report as XLSX.",
}

const MT4_FILE_UPLOAD_OPTION: ConnectBrokerImportMethodOption = {
  id: "file_upload",
  title: "File upload",
  description: "Import a MetaTrader 4 Detailed Report as HTML (.htm or .html).",
}

const FILE_UPLOAD_OPTION = CONNECT_BROKER_FILE_UPLOAD_OPTION

const MANUAL_OPTION: ConnectBrokerImportMethodOption = {
  id: "manual",
  title: "Add manually",
  description:
    "Enter trades one row at a time using Tessera's trade form. Ideal when you only have a few fills.",
}

function resolveFileUploadOption(
  cap: BrokerCapabilities
): ConnectBrokerImportMethodOption {
  const slug = cap.broker_name
  if (isMt5Broker(slug)) return MT5_FILE_UPLOAD_OPTION
  if (isMt4Broker(slug)) return MT4_FILE_UPLOAD_OPTION
  return FILE_UPLOAD_OPTION
}

function manualTradeEntrySupported(cap: BrokerCapabilities): boolean {
  const explicit = cap.manual_trade_entry?.supported
  if (explicit === true) return true
  if (explicit === false) return false

  const hasAutoSync = !!cap.auth?.supports_auto_sync
  const hasFileUpload = !!resolveBrokerCsvImport(cap)?.supported

  if (!hasAutoSync && !hasFileUpload) return true
  return isCustomCsvBroker(cap)
}

/** Import methods offered for a broker in the connect wizard (step 2). */
export function resolveConnectBrokerImportMethods(
  cap: BrokerCapabilities | null | undefined
): ConnectBrokerImportMethodOption[] {
  if (!cap) return []

  const methods: ConnectBrokerImportMethodOption[] = []

  if (cap.auth?.supports_auto_sync) {
    methods.push(AUTO_SYNC_OPTION)
  }

  if (resolveBrokerCsvImport(cap)?.supported) {
    methods.push(resolveFileUploadOption(cap))
  }

  if (manualTradeEntrySupported(cap)) {
    methods.push(MANUAL_OPTION)
  }

  return methods
}
